/**
 * WebAuthn browser glue.
 *
 * The ceremony (base64url ⇄ ArrayBuffer + `navigator.credentials`) now
 * lives in the framework-agnostic client SDK so non-React consumers can
 * use it too. This module re-exports it under the kit's historical names
 * for backward compatibility — prefer the `passkeys(client)` facade from
 * `@nexys/user-management-client` for new code.
 */
export {
  browserSupportsPasskeys as webauthnSupported,
  createPasskeyCredential as createPasskey,
  getPasskeyAssertion,
} from "@nexys/user-management-client";

/**
 * Translate WebAuthn DOMExceptions into sentences a person can act on.
 * The browser's own wording for `InvalidStateError` ("The user
 * attempted to register an authenticator that contains one of the
 * credentials already registered with the relying party") reads like a
 * server bug when it's really the device refusing a duplicate.
 */
export const webauthnErrorMessage = (err: unknown, fallback: string): string => {
  const name = err instanceof Error ? err.name : "";
  if (name === "InvalidStateError") {
    return (
      "This device already holds a passkey for this site — possibly for " +
      "another account. Try signing in with it instead, or remove it from " +
      "your device's password manager before registering a new one."
    );
  }
  if (name === "NotAllowedError" || name === "AbortError") {
    return "The passkey prompt was cancelled or timed out.";
  }
  return err instanceof Error ? err.message : fallback;
};
